import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import * as userActions from '@payaca/store/user/userActions';

import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';

import {
  getIsRequiredFieldValidator,
  getPhoneNumberFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';
import { normalisePhoneNumber } from '@payaca/helpers/phoneNumberHelper';

import { Button } from '@payaca/ambrosia';
import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import PhoneInputField from '../phoneInputField/PhoneInputField';

import './SetupUserForm.sass';

const initialFormState = {};

const SetupUserForm: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const region = useSelector(
    (state: any) => state.users.myProfile?.accounts?.[0]?.region
  );

  const [setupUserErrorMessage, setSetupUserErrorMessage] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);

  const fieldValidators = useMemo(() => {
    const isRequiredFieldValidator = getIsRequiredFieldValidator();

    return {
      contactNumber: [isRequiredFieldValidator, getPhoneNumberFieldValidator()],
    };
  }, []);

  const onSubmit = useCallback(
    (formState: { [key: string]: any }) => {
      setIsProcessing(true);
      setSetupUserErrorMessage(undefined);
      const normalisedContactNumber = normalisePhoneNumber(
        formState.contactNumber,
        region
      );
      const payload = {
        contactNumber: normalisedContactNumber
          ? `+${normalisedContactNumber}`
          : formState.contactNumber,
      };

      dispatch(
        userActions.requestUpdateProfile(payload, (error: any) => {
          setIsProcessing(false);

          if (!error) {
            history.push('/');
          } else {
            setSetupUserErrorMessage(
              'Sorry, there was an error updating your details. Please try again.'
            );
          }
        })
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'history'
    [dispatch, region]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <>
          <ValidatedFieldWrapper
            validationResult={validationState['contactNumber']}
            isTouched={touchedState['contactNumber'] || false}
          >
            <PhoneInputField
              styleVariant={InputStyleVariant.OUTSIZE}
              name="contactNumber"
              value={formState.contactNumber}
              label={'Phone number'}
              isRequired={true}
              onChange={onFieldChange}
              onTouch={onFieldTouch}
              disableValidation={
                validationState['contactNumber']
                  ? !validationState['contactNumber'].isValid
                  : false
              }
            />
          </ValidatedFieldWrapper>

          <div className="button-wrapper">
            <Button
              isProcessing={isProcessing}
              replaceContentWhenProcessing
              onClick={() => !isProcessing && onSubmit(formState)}
              disabled={!isValid}
            >
              Continue
            </Button>
            {setupUserErrorMessage && (
              <ErrorMessage message={setupUserErrorMessage} />
            )}
          </div>
        </>
      );
    },
    [isProcessing, setupUserErrorMessage, onSubmit]
  );

  return (
    <div className="setup-user-form-container">
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
        fieldValidators={fieldValidators}
      />
    </div>
  );
};

export default SetupUserForm;
